
import _ from 'lodash'
import pluralize from 'pluralize'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { withLegacyRouter } from '../../../../decorators'
import http from '../../../../lib/http'
import { fromFHIR, toFHIR } from '../../../../lib/Proxy/allergies.js'
import { useAppDispatch, useAppSelector } from '../../../../store'
import * as Card from '../../../shared/card/index.ts'
import { setListEmpty } from '../../../shared/CrudTable/actions.js'
import CrudTable from '../../../shared/CrudTable/index.jsx'
import ConfirmationDeleteDialog from '../../../shared/DeleteDialog/ConfirmationDeleteDialog.jsx'
import MagicGrid from '../../../shared/MagicGrid/index.jsx'
import { Checkbox, CircularProgress } from '../../../shared/NarusMaterial'
import { compartmentReceive } from '../../patientActions.js'

import AllergyForm from './form'

const tableDefinition = [
  {
    title: 'Allergy Name',
    displayFn: v => {
      if (v.freeText) {
        return v.freeText
      }
      return _.capitalize(_.get(v, 'coding.display'))
    }
  },
  {
    title: 'Category',
    displayFn: v => _.capitalize(v.category)
  },
  {
    title: 'Severity',
    displayFn: v => _.capitalize(v.severity)
  },
  {
    title: 'Reaction',
    displayFn: v => _.capitalize(v.reaction)
  }
]

function Allergies ({
  forBanner,
  inModal
}) {
  const magicGrid = useRef()
  const { isListEmpty, loading, data } = useAppSelector((redux) => ({
    isListEmpty: _.get(redux, 'crudTable.allergies.additionalData.empty', false),
    data: _.get(redux, 'crudTable.allergies.entries', []),
    loading: _.get(redux, 'crudTable.allergies.getNewEntries', true)
  }))
  const dispatch = useAppDispatch()
  const params = useParams()
  const patientId = params.id

  const urls = useMemo(() => ({
    get: () => `/patients/${patientId}/lists/allergies`,
    create: () => `/patients/${patientId}/lists/allergies`,
    update: entry => `/patients/${patientId}/lists/allergies/${entry.id}`,
    delete: entry => `/patients/${patientId}/lists/allergies/${entry.id}`
  }), [ patientId ])

  const mapFns = {
    toForm: fromFHIR,
    toFhir: entry => toFHIR(entry, patientId)
  }

  return (
    <div className='main allergies-container'>
      <Card.Card>
        <div className='component-header'>
          {!inModal && (
            <Card.Header>
              <h2>Allergies</h2>
              <Checkbox
                label='Patient reports no allergies'
                value={isListEmpty}
                disabled={loading || data.length}
                onChange={val => {
                  dispatch(
                    setListEmpty(
                      'allergy',
                      `/patients/${patientId}/lists/allergies`,
                      params.clientName,
                      val,
                      urls,
                      mapFns,
                      () => dispatch(compartmentReceive(patientId))
                    )
                  )
                }}
              />
            </Card.Header>
          )}
        </div>

        <div style={{ padding: '0 1rem 1rem 1rem' }}>
          {forBanner
            ? <MagicGridComponent magicGrid={magicGrid} />
            : (
              <CrudTable
                entryType='allergy'
                entrySet={patientId}
                tableDefinition={tableDefinition}
                urlFns={urls}
                mapFns={mapFns}
                form={AllergyForm}
                disabled={isListEmpty}
                deleteDialog={ConfirmationDeleteDialog}
                additionalDeleteDialogProps={{ prompt: 'Really delete?' }}
                readOnly={inModal}
                callbackFn={() => {
                  dispatch(compartmentReceive(patientId))
                }}
              />
            )}
        </div>
      </Card.Card>
    </div>
  )
}

function MagicGridComponent ({ magicGrid }) {
  const [ data, setData ] = useState(null)
  const params = useParams()
  const patientId = params.id

  useEffect(() => {
    http.getRequest(`/patients/${patientId}/lists/allergies`)
      .then(response => {
        setData(response.entries.map(entry => fromFHIR(entry)))
      })
  }, [ patientId ])

  if (data) {
    return (
      <MagicGrid
        ref={magicGrid}
        rows={data || []}
        headers={tableDefinition}
        itemName={pluralize.plural(_.capitalize('allergy'))}
      />
    )
  }

  return (
    <CircularProgress />
  )
}

export default withLegacyRouter(Allergies)
